import React, { useState, useEffect } from 'react';

/**
 * KinYourFunds
 *
 * The member's personal funding view for a Kin.
 *
 * Shows:
 *   - Your position (available balance, pending pledges)
 *   - The Kin's collective position (total, available, deposit)
 *   - Your deposit reference (with suffix editor)
 *   - The bank details for making a deposit
 *   - Action: pledge a contribution
 *
 * If the viewer is the KC:
 *   - Additionally shows the itemised member positions
 *   - Their own row is marked "(you)"
 */
export default function KinYourFunds({ kinId }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  const [showContribute, setShowContribute] = useState(false);
  const [contributeAmount, setContributeAmount] = useState('');
  const [contributeNote, setContributeNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [flash, setFlash] = useState(null);

  const [depositRef, setDepositRef] = useState(null);
  const [showSuffixEditor, setShowSuffixEditor] = useState(false);
  const [suffixDraft, setSuffixDraft] = useState('');
  const [savingSuffix, setSavingSuffix] = useState(false);

  useEffect(() => {
    if (kinId) {
      fetchFunding();
      fetchReference();
   }
  }, [kinId]);

  const fetchFunding = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`/api/kins/${kinId}/funding`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setData(await res.json());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchReference = async () => {
    try {
      const res = await fetch(`/api/kins/${kinId}/my-deposit-reference`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      if (!res.ok) return;
      const result = await res.json();
      setDepositRef(result.reference);
    } catch (err) {
      // Non-fatal
      console.error('Error fetching deposit reference:', err);
    }
  };

  const submitPledge = async (e) => {
    e.preventDefault();
    setSubmitError(null);

    const amount = parseFloat(contributeAmount);
    if (!Number.isFinite(amount) || amount <= 0) {
      setSubmitError('Enter a valid amount');
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(`/api/kins/${kinId}/contributions/pledge`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({
          amount,
          note: contributeNote || undefined,
        }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || 'Failed to pledge');
      }
      const result = await res.json();
      setFlash(
        `Pledged $${amount.toFixed(2)}. Deposit using reference ${
          result.contribution?.pledgeReference || depositRef?.reference || ''
        }`
      );
      setShowContribute(false);
      setContributeAmount('');
      setContributeNote('');
      await fetchFunding();
    } catch (err) {
      setSubmitError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const saveSuffix = async () => {
    setSavingSuffix(true);
    try {
      const res = await fetch(`/api/kins/${kinId}/my-deposit-reference`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({ suffix: suffixDraft }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || 'Failed to save');
      }
      const result = await res.json();
      setDepositRef(result.reference);
      setShowSuffixEditor(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setSavingSuffix(false);
    }
  };

  const copyText = (text) => {
    try {
      navigator.clipboard.writeText(text);
      setFlash('Copied to clipboard');
    } catch (err) {
      console.error(err);
    }
  };

  const copyDepositInstructions = () => {
    if (!data?.paymentDetails || !depositRef) return;
    const p = data.paymentDetails;
    const text = [
      `BSB: ${p.bsb}`,
      `Account: ${p.accountNumber}`,
      `Name: ${p.accountName}`,
      `Reference: ${depositRef.reference}`,
    ].join('\n');
    copyText(text);
  };

  if (loading) {
    return <div className="text-center py-8">Loading your funds...</div>;
  }
  if (error) {
    return <div className="text-center py-8 text-red-600">Error: {error}</div>;
  }
  if (!data) {
    return <div className="text-center py-8">No data available</div>;
  }

  const isKC = data.role === 'kc';
  const fmt = (n) => '$' + (Number(n) || 0).toFixed(2);
  const pd = data.paymentDetails;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center flex-wrap gap-2">
        <h2 className="text-2xl font-bold">💵 Your Kin Funds</h2>
        {!showContribute && (
          <button
            onClick={() => setShowContribute(true)}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
          >
            + Contribute
          </button>
        )}
      </div>

      {flash && (
        <div className="bg-green-50 border border-green-200 text-green-800 rounded-lg p-3 text-sm">
          ✅ {flash}
        </div>
      )}

      {/* Your position */}
      <div className="bg-white rounded-lg shadow p-4">
        <h3 className="font-semibold text-gray-700 mb-3">Your position</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <div>
            <div className="text-sm text-gray-500">Available</div>
            <div className="text-2xl font-bold text-green-700">
              {fmt(data.myAvailable)}
            </div>
          </div>
          <div>
            <div className="text-sm text-gray-500">Pending contributions</div>
            <div className="text-2xl font-bold text-amber-700">
              {fmt(data.myPendingPledge)}
            </div>
            {data.myPendingCount > 0 && (
              <div className="text-xs text-gray-500 mt-1">
                {data.myPendingCount} pledge
                {data.myPendingCount !== 1 ? 's' : ''} awaiting deposit
              </div>
            )}
          </div>
          {isKC && data.mySecurityDeposit > 0 && (
            <div>
              <div className="text-sm text-gray-500">Your security deposit</div>
              <div className="text-2xl font-bold text-amber-800">
                {fmt(data.mySecurityDeposit)}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* The Kin's position */}
      <div className="bg-white rounded-lg shadow p-4">
        <h3 className="font-semibold text-gray-700 mb-3">The Kin's position</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <div>
            <div className="text-sm text-gray-500">Total balance</div>
            <div className="text-2xl font-bold text-blue-700">
              {fmt(data.totalAvailable)}
            </div>
          </div>
          <div>
            <div className="text-sm text-gray-500">Available for orders</div>
            <div className="text-2xl font-bold text-green-700">
              {fmt(data.availableForOrders)}
            </div>
          </div>
          <div>
            <div className="text-sm text-gray-500">Security deposit</div>
            <div className="text-2xl font-bold text-amber-800">
              {fmt(data.securityDeposit?.total || 0)}
            </div>
          </div>
        </div>
      </div>

      {/* Deposit reference */}
      {depositRef && pd && (
        <div className="bg-white rounded-lg shadow p-4 border-2 border-blue-200">
          <div className="flex items-start justify-between gap-2 mb-3">
            <h3 className="font-semibold text-gray-700">
              🏦 Your deposit reference
            </h3>
            <button
              onClick={() => copyDepositInstructions()}
              className="text-sm text-blue-600 hover:text-blue-800 font-medium"
            >
              📋 Copy all
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
            <div>
              <span className="text-gray-500">BSB:</span>{' '}
              <span className="font-mono font-medium">{pd.bsb}</span>
            </div>
            <div>
              <span className="text-gray-500">Account:</span>{' '}
              <span className="font-mono font-medium">{pd.accountNumber}</span>
            </div>
            <div className="md:col-span-2">
              <span className="text-gray-500">Account name:</span>{' '}
              <span className="font-medium">{pd.accountName}</span>
            </div>
            <div className="md:col-span-2 flex items-center gap-2 flex-wrap">
              <span className="text-gray-500">Reference:</span>
              <code className="bg-blue-50 text-blue-800 px-3 py-1 rounded font-bold text-base">
                {depositRef.reference}
              </code>
              <button
                onClick={() => copyText(depositRef.reference)}
                className="text-xs text-blue-600 hover:text-blue-800"
              >
                Copy
              </button>
              <button
                onClick={() => {
                  setSuffixDraft(depositRef.suffix || '');
                  setShowSuffixEditor(true);
                }}
                className="text-xs text-gray-500 hover:text-gray-700 ml-auto"
              >
                Change suffix
              </button>
            </div>
          </div>

          <p className="text-xs text-gray-500 mt-3">
            The <span className="font-mono">{depositRef.kinCode}{depositRef.memberNumber}</span> part is
            fixed — it identifies you and your Kin. The last part is yours —
            use any letters you'll remember.
          </p>

          {showSuffixEditor && (
            <div className="mt-3 pt-3 border-t flex items-center gap-2">
              <input
                type="text"
                value={suffixDraft}
                onChange={(e) => setSuffixDraft(e.target.value.toUpperCase().replace(/[^A-Z]/g, '').slice(0, 8))}
                className="px-3 py-1.5 border rounded font-mono"
                placeholder="JOE"
                maxLength={8}
              />
              <button
                onClick={saveSuffix}
                disabled={savingSuffix}
                className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 disabled:bg-gray-400"
              >
                {savingSuffix ? 'Saving...' : 'Save'}
              </button>
              <button
                onClick={() => setShowSuffixEditor(false)}
                className="px-3 py-1.5 bg-gray-200 text-gray-700 text-sm rounded hover:bg-gray-300"
              >
                Cancel
              </button>
            </div>
          )}
        </div>
      )}

      {/* Contribute form */}
      {showContribute && (
        <div className="bg-white rounded-lg shadow p-4 border-2 border-green-200">
          <h3 className="font-semibold text-gray-700 mb-3">
            Pledge a contribution
          </h3>
          <form onSubmit={submitPledge} className="space-y-3">
            <div>
              <label className="block text-sm text-gray-600 mb-1">
                Amount (AUD)
              </label>
              <input
                type="number"
                step="0.01"
                min="0.01"
                value={contributeAmount}
                onChange={(e) => setContributeAmount(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500"
                placeholder="100.00"
                required
              />
            </div>
            <div>
              <label className="block text-sm text-gray-600 mb-1">
                Note (optional)
              </label>
              <input
                type="text"
                value={contributeNote}
                onChange={(e) => setContributeNote(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500"
                placeholder="For this week's order"
                maxLength={200}
              />
            </div>
            {submitError && (
              <div className="text-sm text-red-600">{submitError}</div>
            )}
            <p className="text-xs text-gray-500">
              A pledge records your intent. Deposit the funds to the account
              above using your reference, and your contribution will be
              credited once the deposit lands.
            </p>
            <div className="flex gap-2">
              <button
                type="submit"
                disabled={submitting}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400"
              >
                {submitting ? 'Pledging...' : 'Pledge'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowContribute(false);
                  setContributeAmount('');
                  setContributeNote('');
                  setSubmitError(null);
                }}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
